import { registerDecorators as _registerDecorators, LightningElement, registerComponent as _registerComponent } from "lwc";
import _tmpl from "./skillFileBrowser.html"; // LEFT PANE of the Package Files viewer. Renders the file tree, search input, and collapse/expand
// toggle. Presentation + user input only — no fetching.
import { aiAssetEngagement, aiAssetSearch } from '~/modules/analytics/gtmEventTypes/aiAssetAnalyticsContract';
import { WireI18n } from '~/modules/stores/i18nStore';
import { debounce } from '~/modules/utilities/browser';
const SEARCH_ANALYTICS_DEBOUNCE_MS = 300;
function normalizeSearchQuery(query) {
  const searchTerm = query.trim();
  return {
    searchTerm,
    filterTerm: searchTerm.toLowerCase()
  };
}
class SkillFileBrowser extends LightningElement {
  constructor(...args) {
    super(...args);
    this.nodes = [];
    this.selectedPath = '';
    this.treeIsEmpty = false;
    // Local search state
    this.searchQuery = '';
    this.labels = void 0;
  }
  // Handler for wds-file-tree 'select' event.
  handleTreeSelect(event) {
    this.dispatchEvent(new CustomEvent('select', {
      detail: {
        path: event.detail.path
      }
    }));
  }

  // Handler for the search box's `input` event (fires on every keystroke for live filtering).
  handleSearchInput(event) {
    const query = event.detail.value;
    this.searchQuery = query;
    this.searchAnalyticsTimer = debounce(this, this.dispatchSearchAnalytics, [query], this.searchAnalyticsTimer, SEARCH_ANALYTICS_DEBOUNCE_MS);
  }
  disconnectedCallback() {
    window.clearTimeout(this.searchAnalyticsTimer);
  }

  // Debounced analytics ping for the file-search filter, mirrors ace/filterSearch's
  // dispatchFilterChangeEvent so file-search filtering shows up the same way keyword filtering does.
  dispatchSearchAnalytics(query) {
    const {
      searchTerm,
      filterTerm
    } = normalizeSearchQuery(query);
    if (!searchTerm) {
      return;
    }
    this.dispatchEvent(new CustomEvent('analytics', {
      detail: {
        value: query,
        eventType: 'filterKeywordChange',
        elementType: 'skillFileSearchInput',
        aiAssetAnalytics: aiAssetSearch({
          searchTerm,
          searchResultCount: this.countFiles(this.filterNodes(this.nodes, filterTerm)),
          searchCategory: 'files'
        })
      },
      composed: true,
      bubbles: true,
      cancelable: true
    }));
  }

  // Handler for the collapse/expand toggle button.
  handleToggleTree() {
    this.dispatchEvent(new CustomEvent('toggletree'));
  }
  handleTreeToggleAnalytics(event) {
    event.detail.aiAssetAnalytics = aiAssetEngagement({
      clickText: 'collapse file view',
      elementTitle: 'Files',
      elementType: 'sidebar toggle'
    });
  }
  handleTreeAnalytics(event) {
    if (event.detail?.eventType !== 'click') {
      return;
    }
    const path = String(event.detail.value ?? '');
    const leaf = path.split('/').filter(Boolean).pop() ?? path;
    const isFolder = event.detail.elementType === 'fileTreeDir';
    event.detail.aiAssetAnalytics = aiAssetEngagement({
      clickText: isFolder ? `${leaf}/` : leaf,
      elementTitle: 'Files',
      elementType: isFolder ? 'folder' : 'file'
    });
  }

  // The orchestrator calls this after expanding the tree so keyboard focus follows the control
  // from the viewer's right-arrow button back to this left-arrow button. wds-icon-interactive's
  // host does not delegate focus through its analytics wrapper, so target the inner ax control.
  focusTreeToggle() {
    this.template?.querySelector('wds-icon-interactive')?.focusButton();
  }

  // Filtered tree nodes (for wds-file-tree), applying the search query.
  get filteredNodes() {
    const {
      filterTerm
    } = normalizeSearchQuery(this.searchQuery);
    if (!filterTerm) {
      return this.nodes;
    }
    return this.filterNodes(this.nodes, filterTerm);
  }

  // True when a search query is active. Passed to wds-file-tree so it auto-expands folders while
  // filtering (matches nested in folders stay reachable); otherwise folders default to collapsed.
  get isFiltering() {
    return Boolean(normalizeSearchQuery(this.searchQuery).filterTerm);
  }
  get hasNoFilteredResults() {
    return this.isFiltering && this.filteredNodes.length === 0;
  }

  // Filter nodes by search query (case-insensitive substring on name). Keep ancestor directories
  // of any matching descendant so matches stay reachable.
  filterNodes(nodes, query) {
    const result = [];
    for (const node of nodes) {
      if (node.name.toLowerCase().includes(query)) {
        // Node matches: include it (and its children if it's a dir).
        result.push(node);
      } else if (node.type === 'dir' && node.children) {
        // Node doesn't match, but recurse into children.
        const filteredChildren = this.filterNodes(node.children, query);
        if (filteredChildren.length > 0) {
          // At least one descendant matches: keep this dir as an ancestor.
          result.push({
            ...node,
            children: filteredChildren
          });
        }
      }
    }
    return result;
  }
  countFiles(nodes) {
    return nodes.reduce((count, node) => count + (node.type === 'file' ? 1 : this.countFiles(node.children ?? [])), 0);
  }

  // Getters for i18n labels.
  get packageFilesHeading() {
    return this.labels?.packageFilesHeading ?? '';
  }
  get packageFilesTreeLabel() {
    return this.labels?.packageFilesTreeLabel ?? '';
  }
  get packageFilesSearchPlaceholder() {
    return this.labels?.packageFilesSearchPlaceholder ?? '';
  }
  get packageFilesSearchIcon() {
    return this.labels?.packageFilesSearchIcon ?? '';
  }
  get packageFilesCollapseTree() {
    return this.labels?.packageFilesCollapseTree ?? '';
  }
  get packageFilesEmpty() {
    return this.labels?.packageFilesEmpty ?? '';
  }
  get packageFilesNoMatches() {
    return this.labels?.packageFilesNoMatches ?? '';
  }
  /*LWC compiler v7.1.3*/
}
_registerDecorators(SkillFileBrowser, {
  publicProps: {
    nodes: {
      config: 0
    },
    selectedPath: {
      config: 0
    },
    treeIsEmpty: {
      config: 0
    }
  },
  publicMethods: ["focusTreeToggle"],
  wire: {
    labels: {
      adapter: WireI18n,
      dynamic: [],
      config: function ($cmp) {
        return {
          selector: i18n => i18n.skills
        };
      }
    }
  },
  fields: ["searchQuery"]
});
const __lwc_component_class_internal = _registerComponent(SkillFileBrowser, {
  tmpl: _tmpl,
  sel: "",
  apiVersion: 62
});
export default __lwc_component_class_internal;